/**
 * surveyContext — stores all survey question answers during the onboarding flow.
 * Each survey screen reads and writes to this context.
 * Call resetSurvey() to clear state (e.g. on logout).
 */
import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  type PropsWithChildren,
} from 'react';
import type {
  SurveyContextValue,
  SurveyState,
  SurveyAnswerKey,
} from '../types';
import { STORAGE_KEYS } from '../constants';
import { storage } from '../platform/storage';
import { eventBus } from '../platform/eventBus';
import { USER_SESSION_CLEARED_EVENT } from '../api/auth/clearUserSession';

// ─── Initial State ────────────────────────────────────────────────────────────
const initialSurveyState: SurveyState = {
  mainGoal: null,
  poundsToLose: null,
  commitmentLevel: null,
  buddyType: null,
  communicationStyle: null,
  checkInFrequency: null,
  genderPreference: null,
  ageRange: null,
  personalizationRatings: null,
};

// ─── Actions ─────────────────────────────────────────────────────────────────
const SURVEY_ACTIONS = {
  SET_ANSWER: 'SET_ANSWER',
  HYDRATE: 'HYDRATE',
  RESET: 'RESET',
} as const;

type SurveyAction =
  | { type: typeof SURVEY_ACTIONS.SET_ANSWER; key: SurveyAnswerKey; value: SurveyState[SurveyAnswerKey] }
  | { type: typeof SURVEY_ACTIONS.HYDRATE; payload: SurveyState }
  | { type: typeof SURVEY_ACTIONS.RESET };

// ─── Reducer ──────────────────────────────────────────────────────────────────
const surveyReducer = (state: SurveyState, action: SurveyAction): SurveyState => {
  switch (action.type) {
    case SURVEY_ACTIONS.SET_ANSWER:
      return { ...state, [action.key]: action.value };
    case SURVEY_ACTIONS.HYDRATE:
      return { ...state, ...action.payload };
    case SURVEY_ACTIONS.RESET:
      return { ...initialSurveyState };
    default:
      return state;
  }
};

// ─── Context ──────────────────────────────────────────────────────────────────
const surveyContext = createContext<SurveyContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────
export const SurveyProvider = ({
  children,
}: PropsWithChildren): React.JSX.Element => {
  const [surveyData, dispatch] = useReducer(surveyReducer, initialSurveyState);

  // Restore survey state from persistent storage on mount
  useEffect(() => {
    const loadSurveyData = async () => {
      try {
        const stored = await storage.getItem<string>(STORAGE_KEYS.SURVEY_DATA);
        if (stored) {
          const parsed = typeof stored === 'string' ? JSON.parse(stored) : stored;
          if (parsed && typeof parsed === 'object') {
            dispatch({ type: SURVEY_ACTIONS.HYDRATE, payload: parsed });
          }
        }
      } catch {
        /* ignore storage load errors */
      }
    };
    loadSurveyData();
  }, []);

  const setSurveyAnswer = useCallback(
    <K extends SurveyAnswerKey>(key: K, value: SurveyState[K]) => {
      dispatch({ type: SURVEY_ACTIONS.SET_ANSWER, key, value });
    },
    [],
  );

  // Persist changes to storage
  useEffect(() => {
    if (Object.values(surveyData).some(val => val !== null)) {
      storage.setItem(STORAGE_KEYS.SURVEY_DATA, JSON.stringify(surveyData));
    }
  }, [surveyData]);

  const resetSurvey = useCallback(() => {
    storage.removeItem(STORAGE_KEYS.SURVEY_DATA);
    dispatch({ type: SURVEY_ACTIONS.RESET });
  }, []);

  // Logout / session wipe — do not key off isAuthenticated (onboarding uses survey while signed-out).
  useEffect(() => {
    const sub = eventBus.addListener(USER_SESSION_CLEARED_EVENT, () => {
      dispatch({ type: SURVEY_ACTIONS.RESET });
    });
    return () => sub.remove();
  }, []);

  const hydrateSurvey = useCallback((payload: SurveyState) => {
    dispatch({ type: SURVEY_ACTIONS.HYDRATE, payload });
  }, []);

  return (
    <surveyContext.Provider value={{ surveyData, setSurveyAnswer, resetSurvey, hydrateSurvey }}>
      {children}
    </surveyContext.Provider>
  );
};

export const useSurvey = (): SurveyContextValue => {
  const survey = useContext(surveyContext);
  if (!survey) {
    throw new Error('useSurvey must be used inside <SurveyProvider>');
  }
  return survey;
};

export default surveyContext;
